import { useMemo } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { ConfluenceEdition } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { UNSAFE_noExposureExp } from '@confluence/feature-experiments';

import { useReverseTrialLozengeEligible } from './useReverseTrialLozengeEligible.ts';

interface ReverseTrialLozengeExperimentProps {
	cohort: ExperimentCohort;
	experimentName: string;
	experimentExposure: () => void;
	isGateEnabled: boolean;
	isUserInAudience: boolean;
	touchpointId: string;
	edition: ConfluenceEdition;
	shouldFireExposure?: boolean;
	loading: boolean;
	error?: string | null;
}

export enum ExperimentCohort {
	NOT_ENROLLED = 'not-enrolled',
	CONTROL = 'control',
	TREATMENT = 'treatment',
}

const useReverseTrialLozengeExperiment = ({
	cohort,
	experimentName,
	experimentExposure,
	isGateEnabled,
	isUserInAudience,
	touchpointId,
	edition,
	shouldFireExposure = true,
	loading,
	error,
}: ReverseTrialLozengeExperimentProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isEligible = isGateEnabled && cohort !== ExperimentCohort.NOT_ENROLLED && isUserInAudience;

	return useMemo(() => {
		if (!isEligible) {
			return {
				cohort,
				experimentName,
				renderTreatment: false,
				loading,
				error,
			};
		}

		if (shouldFireExposure) {
			experimentExposure();

			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'exposed',
					actionSubject: 'experiment',
					actionSubjectId: `${experimentName}-exposed`,
					attributes: {
						cohort,
						touchpointId,
						edition,
					},
					source: experimentName,
				},
			}).fire();
		}

		return {
			cohort,
			experimentName,
			renderTreatment: isEligible && cohort === ExperimentCohort.TREATMENT,
			loading,
			error,
		};
	}, [
		isEligible,
		createAnalyticsEvent,
		experimentName,
		touchpointId,
		cohort,
		experimentExposure,
		edition,
		shouldFireExposure,
		loading,
		error,
	]);
};

export const useReverseTrialLozengeExperimentAb = ({
	touchpointId,
	edition,
	shouldFireExposure,
}: {
	touchpointId: string;
	edition: ConfluenceEdition;
	shouldFireExposure?: boolean;
}) => {
	const { edition: currentUserEdition } = useSessionData();
	const { experimentName, cohort, experimentExposure, isGateEnabled } = getEditionExperiment({
		currentUserEdition,
		touchpointId,
		touchpointEdition: edition,
	});

	const {
		isEligible,
		loading: isAbEligibleLoading,
		error: hasAbEligibleError,
	} = useReverseTrialLozengeEligible({
		experimentName,
		isGateEnabled,
	});

	return useReverseTrialLozengeExperiment({
		cohort,
		experimentName,
		experimentExposure,
		shouldFireExposure,
		isGateEnabled,
		isUserInAudience: isEligible,
		touchpointId,
		edition,
		loading: isAbEligibleLoading,
		error: hasAbEligibleError,
	});
};

type EditionExperiment = {
	experimentName: string;
	cohort: ExperimentCohort;
	experimentExposure: () => void;
	isGateEnabled: boolean;
};

const getEditionExperiment = ({
	currentUserEdition,
	touchpointId,
	touchpointEdition,
}: {
	currentUserEdition: ConfluenceEdition | null;
	touchpointId: string;
	touchpointEdition: ConfluenceEdition;
}): EditionExperiment => {
	switch (currentUserEdition) {
		case ConfluenceEdition.STANDARD: {
			const [experimentConfig, experimentExposure] = UNSAFE_noExposureExp(
				'confluence_standard_trial_no_pde_lozenges',
			);
			const cohort = experimentConfig.get(
				'cohort',
				ExperimentCohort.NOT_ENROLLED,
			) as ExperimentCohort;

			// NOTE: Special handling for automation touchpoints, there are places in product exposing them as premium edition
			// However, for this experiment, we want to surface them as standard paid edition feature
			const automationTouchPoint =
				touchpointId === 'confluence_automation_menu_item' ||
				touchpointId === 'confluence_space_settings_automation_list_item' ||
				touchpointId === 'confluence_page_automations_list_item' ||
				touchpointId === 'confluence_space_settings_opt_automation_menu_item';

			if (touchpointEdition == ConfluenceEdition.STANDARD || automationTouchPoint) {
				return {
					experimentName: 'confluence_standard_trial_no_pde_lozenges',
					cohort,
					experimentExposure,
					isGateEnabled: fg('confluence_standard_trial_no_pde_lozenges_fg'),
				};
			}

			return {
				experimentName: '',
				cohort: ExperimentCohort.NOT_ENROLLED,
				experimentExposure: () => {},
				isGateEnabled: false,
			};
		}
		default:
			return {
				experimentName: '',
				cohort: ExperimentCohort.NOT_ENROLLED,
				experimentExposure: () => {},
				isGateEnabled: false,
			};
	}
};
